const pluralRules = new Intl.PluralRules('en-US');

const irregularPlurals = {
  reply: 'replies',
};

export const getPluralForm = (count, word) => {
  const pluralCategory = pluralRules.select(count);
  if (count === 0 || pluralCategory === 'other') {
    const plural = irregularPlurals[word] || `${word}s`;
    return `${count} ${plural}`;
  } else if (count === 1) {
    return `1 ${word}`;
  } else {
    const plural = irregularPlurals[word] || `${word}s`;
    return `${count} ${plural}`;
  }
};

// export const abbreviateNumber = (value) => {
//   value = Math.round(value);
//   if (value === 0) return "0";

//   const SI_SYMBOL = ["", "K", "M", "G", "T", "P", "E"];
//   const tier = Math.floor(Math.log10(Math.abs(value)) / 3);

//   if (tier === 0) return value.toString();

//   const suffix = SI_SYMBOL[tier];
//   const scale = Math.pow(10, tier * 3);
//   const scaled = value / scale;

//   if (tier === 1 && value >= 1000 && value < 10000) {
//     return scaled.toFixed(1).replace('.', ',') + suffix;
//   }

//   if (tier >= 1) {
//     if (tier >= 2) {
//       return scaled.toFixed(3).replace('.', ',') + suffix;
//     }

//     return scaled.toFixed(2).replace('.', ',') + suffix;
//   }

//   return scaled.toFixed().replace('.', ',') + suffix;
// };

export const abbreviateNumber = (value, formatType) => {
  value = Math.round(value);

  if (formatType === 'badge') {
    return value.toLocaleString('en').replace(/\./g, ',');
  }

  if (formatType === 'short') {
    if (value >= 1000000) {
      // For millions
      return (value / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    } else if (value >= 10000) {
      // For numbers from 10,000 to 999,999
      return (value / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
    } else {
      // For numbers less than 10,000
      return value.toLocaleString('en');
    }
  }

  return value.toString();
};

// export const formatRating = (rating) => {
//   if (rating >= 1000000) {
//     return (Math.round((rating / 100000) * 10) / 100).toFixed(1).replace(/\.0$/, '') + 'M';
//   } else if (rating > 99000) {
//     return Math.floor(rating / 1000).replace(/\.0$/, '') + 'K';
//   } else if (rating >= 1000) {
//     return (Math.round((rating / 100) * 10) / 100).toFixed(1).replace(/\.0$/, '') + 'K';
//   } else {
//     return rating.toString();
//   }
// };

export const calcPercenteChange = (prev, cur) => {
  return prev !== 0 ? ((cur - prev) / prev) * 100 : 0;
};

// Global cache for user ratings distribution
let userRatingsCache = null;
let lastCacheTime = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

/**
 * Fetches all user ratings from the API to calculate percentiles
 * @returns {Promise<Array<number>>} Array of user ratings sorted in ascending order
 */
export const fetchAllUserRatings = async () => {
  // Check if we have a recent cache
  const now = Date.now();
  if (userRatingsCache && (now - lastCacheTime < CACHE_DURATION)) {
    return userRatingsCache;
  }
  
  try {
    // Fetch all users' ratings from the API
    const response = await fetch('/api/users/ratings');
    const data = await response.json();
    
    // Extract and sort ratings in ascending order
    const ratings = data.map(user => user.rating || 0).sort((a, b) => a - b);
    
    // Update cache
    userRatingsCache = ratings;
    lastCacheTime = now;
    
    return ratings;
  } catch (error) {
    console.error('Error fetching user ratings:', error);
    // Return empty array if there's an error
    return [];
  }
};

/**
 * Converts a score to a percentile based on all users in the database
 * @param {number} score - User's raw score/rating
 * @param {boolean} [asDecimal=false] - If true, returns the percentile as a decimal (0-1)
 * @returns {Promise<number>} - The percentile (0-100 or 0-1 based on asDecimal)
 */
export const scoreToPercentile = async (score, asDecimal = false) => {
  const ratings = await fetchAllUserRatings();
  
  if (!ratings.length) {
    return asDecimal ? 0 : 0; // If no ratings, return 0
  }
  
  // Find how many users have scores less than the current user
  const usersBelow = ratings.filter(rating => rating < score).length;
  
  // Calculate percentile
  const percentile = (usersBelow / ratings.length) * (asDecimal ? 1 : 100);
  
  // Round to one decimal place
  return Math.round(percentile * 10) / 10;
};

/**
 * Synchronous version of scoreToPercentile for immediate UI rendering
 * Uses a mock distribution until real data is available from API
 * @param {number} score - User's raw score/rating
 * @param {boolean} [asDecimal=false] - If true, returns the percentile as a decimal (0-1)
 * @returns {number} - The percentile (0-100 or 0-1 based on asDecimal)
 */
export const scoreToPercentileSync = (score, asDecimal = false) => {
  // Mock distribution based on common reputation scoring patterns
  // This can be replaced with actual distribution data when available
  
  if (score <= 0) return asDecimal ? 0 : 0;
  
  // Logarithmic percentile calculation based on typical user score distribution
  // Assumes most users cluster at lower scores with fewer high-scoring users
  const percentile = Math.min(
    Math.log10(score + 1) / Math.log10(10000) * (asDecimal ? 1 : 100),
    asDecimal ? 1 : 100
  );
  
  // Round to one decimal place
  return Math.round(percentile * 10) / 10;
};

/**
 * Formats a percentile value for display
 * @param {number} percentile - Percentile value (0-100)
 * @param {string} [format='default'] - Format style: 'default', 'short', or 'rank'
 * @returns {string} - Formatted percentile string
 */
export const formatPercentile = (percentile, format = 'default') => {
  if (percentile === null || percentile === undefined) return '-';
  
  // Ensure percentile is a number and within valid range
  const pct = Math.max(0, Math.min(100, Number(percentile)));
  
  switch (format) {
    case 'short':
      // Short format just shows the percentile with % symbol
      return `${pct.toFixed(1)}%`;
    
    case 'rank':
      // Rank format shows percentile as text rank
      if (pct >= 99) return 'Top 1%';
      if (pct >= 95) return 'Top 5%';
      if (pct >= 90) return 'Top 10%';
      if (pct >= 75) return 'Top 25%';
      if (pct >= 50) return 'Top 50%';
      return `Bottom ${Math.round(100 - pct)}%`;
    
    case 'ordinal':
      // Returns an ordinal rank (1st, 2nd, 3rd, etc.)
      const rank = Math.ceil(100 - pct);
      const suffixes = ['th', 'st', 'nd', 'rd'];
      const suffix = rank % 100 <= 10 || rank % 100 >= 14 ? suffixes[rank % 10] || 'th' : 'th';
      return `${rank}${suffix}`;
      
    default:
      // Default format shows percentile with ordinal indicator
      return `${pct.toFixed(1)}%`;
  }
};
